import { Newspaper } from "lucide-react";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import "./TopNavBar.css";
import { useNotifications } from "../context/NotificationContext";
import NotificationPopup from "./NotificationPopup";
import apiClient from "../api/apiClient";

import dogExpandIconW from "../assets/topNavBarIcons/dogExpandIconW.svg";
import dogShrinkIconW from "../assets/topNavBarIcons/dogShrinkIconW.svg";
import bookingW from "../assets/topNavBarIcons/bookingW.svg";
import calendarW from "../assets/topNavBarIcons/calendarW.svg";
import journeyW from "../assets/topNavBarIcons/journeyW.svg";
import messageW from "../assets/topNavBarIcons/messageW.svg";
import notificationW from "../assets/topNavBarIcons/notificationW.svg";
import playbookW from "../assets/topNavBarIcons/playbookW.svg";
import dogExpandIconB from "../assets/topNavBarIcons/dogExpandIconB.svg";
import dogShrinkIconB from "../assets/topNavBarIcons/dogShrinkIconB.svg";
import bookingB from "../assets/topNavBarIcons/bookingB.svg";
import calendarB from "../assets/topNavBarIcons/calendarB.svg";
import journeyB from "../assets/topNavBarIcons/journeyB.svg";
import messageB from "../assets/topNavBarIcons/messageB.svg";
import notificationB from "../assets/topNavBarIcons/notificationB.svg";
import playbookB from "../assets/topNavBarIcons/playbookB.svg";
import dogPlaceholder from "../assets/dogPlaceholder.png";
import userPlaceholder from "../assets/userPlaceholder.png";
import homeB from "../assets/topNavBarIcons/homeB.svg";
import homeW from "../assets/topNavBarIcons/homeW.svg";

const TopNavBar = ({ theme = "dark", mobileFeed = false }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showNotifPopup, setShowNotifPopup] = useState(false);
  const navRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useSelector((state) => state.user);
  const [user, setUser] = useState(null);

  const isDark = theme === "dark";

  const { unreadCount, setUnreadCount } = useNotifications();

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    if (!profile?.id) {
      setUser(null);
      return;
    }

    // Always refresh: a cached profile may predate an uploaded account photo.
    const fetchUserDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${profile.id}`, {
          signal: controller.signal,
        });
        if (!cancelled && res.data?.status && res.data.data) {
          const userData = res.data.data;
          setUser({ source: profile, data: userData });
          localStorage.setItem("cached_user_details", JSON.stringify(userData));
          localStorage.setItem("user_dog_status", userData.user_dog_status);
          localStorage.setItem(
            "team_social",
            userData.team_social ?? userData.dogs?.[0]?.team_social ?? 0,
          );
        }
      } catch (err) {
        if (!cancelled) console.error("Error fetching user:", err);
      }
    };

    fetchUserDetails();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [profile, location.pathname]);

  const cachedDogStatus = Number(localStorage.getItem("user_dog_status"));
  const cachedTeamSocial = Number(localStorage.getItem("team_social"));

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (profile?.id) {
        try {
          const response = await apiClient.get(`/notifications/${profile.id}`);
          if (response.data?.status) {
            const data = response.data.data;
            const count = data.filter((n) => !n.is_read).length;
            if (setUnreadCount) {
              setUnreadCount(count);
            }
          }
        } catch (error) {
          console.error("Error fetching notification count:", error);
        }
      }
    };

    fetchUnreadCount();
  }, [profile?.id, location.pathname, setUnreadCount]);

  const toggleNav = () => setIsExpanded(!isExpanded);

  const activeData = user?.source === profile ? user.data : profile;
  const isPupParent =
    Number(activeData?.user_dog_status) === 1 ||
    Number(activeData?.team_paws) === 1 ||
    Number(activeData?.dogs?.[0]?.team_paws) === 1 ||
    (activeData?.user_dog_status == null && cachedDogStatus === 1);

  const getAccountImage = () => {
    if (!activeData) return userPlaceholder;

    const dogImg =
      activeData.dog_images?.[0] || activeData.dogs?.[0]?.dog_images?.[0];
    const userImg =
      activeData.user_profile_image?.[0] ||
      activeData.profile_image?.[0] ||
      activeData.dogs?.[0]?.user_profile_image?.[0] ||
      activeData.dogs?.[0]?.profile_image?.[0];

    if (isPupParent) {
      if (dogImg && dogImg.trim() !== "") return dogImg;
      if (userImg && userImg.trim() !== "") return userImg;
      return dogPlaceholder;
    } else {
      if (userImg && userImg.trim() !== "") return userImg;
      return userPlaceholder;
    }
  };

  const getAccountPlaceholder = () => {
    if (!activeData) return userPlaceholder;
    return isPupParent ? dogPlaceholder : userPlaceholder;
  };

  const accountAvatar = getAccountImage();
  const fallbackAvatar = getAccountPlaceholder();

  const shouldShowPlaybook = useMemo(() => {
    return isPupParent;
  }, [isPupParent]);

  // Check if we are on the dashboard route
  const isDashboard = location.pathname.includes("dashboard-switcher");

  // Navigation Items for Dashboard Pages
  const dashboardItems = [
    { id: "feed", label: "Feed", isFeed: true },
    {
      id: "dog-sharing-journey",
      label: "Journey",
      iconW: journeyW,
      iconB: journeyB,
    },
    ...(shouldShowPlaybook
      ? [
          {
            id: "walkies-and-food",
            label: "Playbook",
            iconW: playbookW,
            iconB: playbookB,
          },
        ]
      : []),
    {
      id: isPupParent ? "team-paws-availability" : "team-social-availability",
      label: "Calendar",
      iconW: calendarW,
      iconB: calendarB,
    },
    // {
    //   id: "dashboard-switcher",
    //   label: "Home",
    //   iconW: homeW,
    //   iconB: homeB,
    // },
    {
      id: "current-bookings",
      label: "Bookings",
      iconW: bookingW,
      iconB: bookingB,
    },
    { id: "messages", label: "Messages", iconW: messageW, iconB: messageB },
    {
      id: "notifications",
      label: "Notifications",
      iconW: notificationW,
      iconB: notificationB,
      badge: unreadCount,
    },
    {
      id: "user-profile",
      label: "Account",
      isAvatar: true,
      icon: accountAvatar,
    },
  ];

  // Navigation Items for Other Pages
  const otherItems = [
    { id: "feed", label: "Feed", isFeed: true },
    // {
    //   id: "sniff-1",
    //   label: "Sniff Around",
    //   iconW: journeyW,
    //   iconB: journeyB,
    // },
    // {
    //   id: "sniff-2",
    //   label: "Sniff Around",
    //   iconW: journeyW,
    //   iconB: journeyB,
    // },
    // { id: "browse-map", label: "Browse", iconW: journeyW, iconB: journeyB },
    // { id: "browse-list", label: "Browse", iconW: journeyW, iconB: journeyB },
    {
      id: "dog-sharing-journey",
      label: "Journey",
      iconW: journeyW,
      iconB: journeyB,
    },
    ...(shouldShowPlaybook
      ? [
          {
            id: "walkies-and-food",
            label: "Playbook",
            iconW: playbookW,
            iconB: playbookB,
          },
        ]
      : []),
    {
      id: isPupParent ? "team-paws-availability" : "team-social-availability",
      label: "Calendar",
      iconW: calendarW,
      iconB: calendarB,
    },
    // {
    //   id: "dashboard-switcher",
    //   label: "Home",
    //   iconW: homeW,
    //   iconB: homeB,
    // },
    {
      id: "current-bookings",
      label: "Bookings",
      iconW: bookingW,
      iconB: bookingB,
    },
    { id: "messages", label: "Messages", iconW: messageW, iconB: messageB },
    {
      id: "notifications",
      label: "Notifications",
      iconW: notificationW,
      iconB: notificationB,
      badge: unreadCount,
    },
    {
      id: "user-profile",
      label: "Account",
      isAvatar: true,
      icon: accountAvatar,
    },
  ];

  // Determine which list of items to use
  const navItems = isDashboard ? dashboardItems : otherItems;
  const displayedItems = mobileFeed
    ? [navItems[0], { id: "dashboard-switcher", label: "Home", iconW: homeW, iconB: homeB, mobileHome: true }, ...navItems.slice(1)]
    : navItems;

  // Determine what to hide based on current page context
  const itemsToHideWhenShrunk = isDashboard
    ? ["journey", "walkies-and-food", "calendar"]
    : [
        "sniff-1",
        "sniff-2",
        "browse-map",
        "browse-list",
        "journey",
        "walkies-and-food",
        "calendar",
      ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setShowNotifPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleItemClick = (item) => {
    // Prevent routing for static items
    if (item.id === "sniff-1" || item.id === "sniff-2") {
      return;
    }

    // Direct routing to Map Dashboard
    if (item.id === "browse-map") {
      const currentView =
        localStorage.getItem("dashboard_current_view") || "map";
      localStorage.setItem("dashboard_previous_view", currentView);
      localStorage.setItem("dashboard_current_view", "map");
      navigate("/dashboard-switcher");
      return;
    }

    // Direct routing to List Dashboard
    if (item.id === "browse-list") {
      const currentView =
        localStorage.getItem("dashboard_current_view") || "list";
      localStorage.setItem("dashboard_previous_view", currentView);
      localStorage.setItem("dashboard_current_view", "list");
      navigate("/dashboard-switcher");
      return;
    }

    if (item.id === "notifications") {
      setShowNotifPopup(!showNotifPopup);
    } else {
      setShowNotifPopup(false);

      if (
        item.id === "team-paws-availability" ||
        item.id === "team-social-availability"
      ) {
        navigate(`/${item.id}`, { state: { fromNavBar: true } });
      } else {
        navigate(`/${item.id}`);
      }
    }
  };

  const dogExpand = isDark ? dogExpandIconW : dogExpandIconB;
  const dogShrink = isDark ? dogShrinkIconW : dogShrinkIconB;

  return (
    <div
      ref={navRef}
      className={`top-nav-container ${isExpanded ? "expanded" : "shrunk"} theme-${theme}`}
    >
      <div className="nav-icons-wrapper">
        {displayedItems.map((item) => {
          const shouldHide =
            !isExpanded && itemsToHideWhenShrunk.includes(item.id);

          const isAccount = item.id === "user-profile";

          if (shouldHide) return null;
          if (isAccount && isExpanded && !mobileFeed) return null;

          return (
            <div
              key={item.id}
              data-id={item.id}
              role={item.isFeed ? "link" : undefined}
              tabIndex={item.isFeed ? 0 : undefined}
              aria-current={item.isFeed && location.pathname === "/feed" ? "page" : undefined}
              onKeyDown={item.isFeed ? (event) => {
                if (event.key === "Enter") handleItemClick(item);
              } : undefined}
              className={`nav-icon-item ${mobileFeed && isAccount && isExpanded ? "feed-mobile-account" : ""} ${item.mobileHome ? "feed-mobile-home" : ""} ${item.id === "notifications" && showNotifPopup ? "active-notif" : ""}`}
              onClick={() => handleItemClick(item)}
            >
              <div className="icon-box">
                {item.isFeed ? (
                  <Newspaper
                    className="item-icon"
                    color={isDark ? "#ffffff" : "#000000"}
                    aria-hidden="true"
                  />
                ) : item.isAvatar ? (
                  <img
                    src={item.icon}
                    alt="Account"
                    className="account-avatar-icon"
                    onError={(e) => {
                      e.target.src = fallbackAvatar;
                    }}
                    style={{
                      border: isDark
                        ? "1px solid rgba(204, 241, 255, 1)"
                        : "1px solid rgba(0, 0, 0, 1)",
                    }}
                  />
                ) : (
                  <img
                    src={isDark ? item.iconW : item.iconB}
                    alt={item.label}
                    className="item-icon"
                  />
                )}
                {item.badge > 0 && (
                  <span className="nav-badge">{item.badge}</span>
                )}
              </div>
              <span className={`icon-label theme-${theme}`}>{item.label}</span>
              {item.id === "notifications" && showNotifPopup && (
                <NotificationPopup onClose={() => setShowNotifPopup(false)} />
              )}
            </div>
          );
        })}
      </div>

      <div className="nav-toggle-section" onClick={toggleNav}>
        <span
          className={`toggle-arrow ${isExpanded ? "left" : "right"}`}
        ></span>

        <div className="dog-toggle-wrapper">
          <img
            src={isExpanded ? dogShrink : dogExpand}
            alt="Toggle Menu"
            className="dog-icon"
          />
        </div>
      </div>
    </div>
  );
};

export default TopNavBar;
